'use client';

import { useMemo } from 'react';
import { Histogram, type HistogramData } from '@charts';
import data from '@/data/sources/pulse/chart_ready.json';
import { highlightKey, pulseHighlights } from '../pulseHighlights';

type Project = {
  id: string;
  category: 'professional' | 'personal';
  points: [number, number, number][]; // [epoch_s, value_log, weight_linear]
};

const pulseData: HistogramData = {
  start: data.start ?? 0,
  end: data.end ?? 1,
  series: (data.projects as unknown as Project[]).map((p) => ({
    id: p.id,
    tone: p.category === 'personal' ? 'muted' : 'strong',
    points: p.points.map(([t, , w]) => [t, w] as [number, number]),
  })),
};

export const PulseChart = ({
  projectIds = null,
  highlightIds = null,
  ariaLabel,
  ...rest
}: {
  projectIds?: string[] | null;
  highlightIds?: string[] | null;
  height?: number;
  scaleToAll?: boolean;
  showTicks?: boolean;
  flipped?: boolean;
  className?: string;
  ariaLabel?: string;
}) => {
  const activeHighlight = highlightIds ? highlightKey(highlightIds) : null;

  // The landing's era groups are prebuilt; an id combo outside them (the
  // design-gallery chips) gets an ad-hoc layer appended.
  const highlights = useMemo(
    () =>
      highlightIds &&
      activeHighlight &&
      !pulseHighlights.some((h) => h.key === activeHighlight)
        ? [...pulseHighlights, { key: activeHighlight, ids: highlightIds }]
        : pulseHighlights,
    [activeHighlight, highlightIds]
  );

  return (
    <Histogram
      data={pulseData}
      filterIds={projectIds}
      highlights={highlights}
      activeHighlight={activeHighlight}
      ariaLabel={ariaLabel ?? 'Build activity over time'}
      {...rest}
    />
  );
};
